import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Form, Navigate, json, redirect, useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './contribute.css'

// COMPONENTS
import * as Icons from '@icons'
import { Column } from '@components/templates/column'
import Avatar from '@components/molecules/avatar'
import CurrencyTextField from '@components/molecules/currency-textfield'
import GroupBanner from '@components/organisims/group-banner'
import GroupProgressBar from '@components/molecules/group-progress-bar'
import PledgeList from '@components/organisims/pledge-list'
import Button from '@components/atoms/button'
import SplitRecommendationCard from '@components/molecules/split-recommendation-card'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { max } from '@constants'
import { parseUserLimits } from '@schemas'
import { toPennies } from '@utils/number-helper'
import { createAuthenticatedLoader, refreshClerkUser } from '@utils/loaders'
import { useCurrencyInput } from '@hooks/inputs'
import { useGroupLoaderData, usePledgeOutletContext } from '..'
import { getGroupStatus } from '@utils/temp-helpers'

interface Props { }

const formId = 'group-create-pledge-default'
const minSplitCount = 2
const maxSplitCount = 20
const getPeopleSplittingCountStorageKey = (groupId: string) => `group-${groupId}-people-splitting-count`
const getSuggestedContributionStorageKey = (groupId: string) => `group-${groupId}-suggested-contribution`
const getParticipantOrganizerModalSeenStorageKey = (groupId: string) => `group-${groupId}-participant-organizer-modal-seen`
const participantOrganizerSteps = [
    {
        label: 'Join the Splitcard',
        description: 'Review details and contribute your share.',
    },
    {
        label: 'Invite Others',
        description: 'Share the link with friends and watch the goal get funded together.',
    },
    {
        label: 'Track Purchase',
        description: 'View and adjust spending as the merchant charges the shared card.',
    },
]
const GroupContributeScreen: FC<Props> = () => {
    const { user: clerkUser, isSignedIn } = useUser()
    const [{ pledge }, setState] = usePledgeOutletContext()
    const group = useGroupLoaderData()
    const navigate = useNavigate()

    const { amount: storedAmount, paymentMethod } = pledge
    const { organizer } = group

    const [pledgeAmount, pledgeAmountProps, setPledgeAmount] = useCurrencyInput({
        initValue: `${(storedAmount || 0) / 100}`, max: max,
        onBlur: (amount) => setState((currentState) => ({ ...currentState, pledgeAmount: amount }))
    })

    const submitable = useMemo(() => (!!pledgeAmount && toPennies(pledgeAmount) >= 100 && toPennies(pledgeAmount) <= max), [pledgeAmount])
    const hasPledge = !!group.pledges?.find(({ user }) => (user?.id === clerkUser?.id))

    const { groupStatus } = getGroupStatus(group)
    const isOrganizer = isSignedIn ? organizer?.id === clerkUser.id : false
    const participantOrganizerRef = useRef<HTMLDialogElement>(null)
    const initializedGroupIdRef = useRef<string>()
    const claimedPledges = useMemo(() => (group.pledges || []).filter((item) => !!item.transactionId || !!item.user?.id), [group.pledges])
    const isParticipantOrganizer = useMemo(() => {
        if (group.type !== 'merchant') return false

        const joinedParticipantCount = claimedPledges.filter((item) => {
            if (item.transactionId && !item.user?.id) return true
            return item.user?.id !== group.merchant?.user?.id
        }).length

        return joinedParticipantCount === 0
    }, [group.type, group.merchant?.user?.id, claimedPledges])
    const defaultPeopleSplittingCount = useMemo(() => {
        const participantSlots = (group.pledges || []).filter((item) => !item.user?.id || item.user.id !== group.merchant?.user?.id).length
        return Math.min(maxSplitCount, Math.max(minSplitCount, participantSlots || minSplitCount))
    }, [group.pledges, group.merchant?.user?.id])
    const [peopleSplittingCount, setPeopleSplittingCount] = useState(defaultPeopleSplittingCount)
    const recommendedContribution = useMemo(() => {
        const targetAmount = group.targetAmount || 0
        return (targetAmount > 0 && peopleSplittingCount > 0)
            ? Math.round(targetAmount / peopleSplittingCount)
            : 0
    }, [group.targetAmount, peopleSplittingCount])

    useEffect(() => {
        if (initializedGroupIdRef.current === group.id) return
        initializedGroupIdRef.current = group.id

        const storedCount = Number(sessionStorage.getItem(getPeopleSplittingCountStorageKey(group.id)))
        const initialPeopleSplittingCount = Number.isFinite(storedCount)
            ? Math.min(maxSplitCount, Math.max(minSplitCount, Math.floor(storedCount)))
            : defaultPeopleSplittingCount

        setPeopleSplittingCount(initialPeopleSplittingCount)
    }, [group.id, defaultPeopleSplittingCount])

    useEffect(() => {
        if (!isParticipantOrganizer) return
        sessionStorage.setItem(getPeopleSplittingCountStorageKey(group.id), `${peopleSplittingCount}`)
    }, [isParticipantOrganizer, group.id, peopleSplittingCount])

    useEffect(() => {
        if (!isParticipantOrganizer) return
        sessionStorage.setItem(getSuggestedContributionStorageKey(group.id), `${Math.max(0, recommendedContribution)}`)
    }, [isParticipantOrganizer, group.id, recommendedContribution])

    useEffect(() => {
        if (!isParticipantOrganizer || !participantOrganizerRef.current || participantOrganizerRef.current.open) return
        if (sessionStorage.getItem(getParticipantOrganizerModalSeenStorageKey(group.id)) === 'true') return
        participantOrganizerRef.current.showModal()
    }, [isParticipantOrganizer, group.id])

    function markParticipantOrganizerModalSeen() {
        sessionStorage.setItem(getParticipantOrganizerModalSeenStorageKey(group.id), 'true')
    }

    function closeParticipantOrganizerModal() {
        markParticipantOrganizerModalSeen()
        if (participantOrganizerRef.current) participantOrganizerRef.current.close()
    }

    function increasePeopleSplittingCount() {
        setPeopleSplittingCount((current) => Math.min(maxSplitCount, current + 1))
    }

    function decreasePeopleSplittingCount() {
        setPeopleSplittingCount((current) => Math.max(minSplitCount, current - 1))
    }

    function applyRecommendedAmount() {
        if (recommendedContribution <= 0) return
        setPledgeAmount((recommendedContribution / 100).toFixed(2))
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault()

        if (pledgeAmount) setState((currentState) => ({
            ...currentState,
            pledge: { ...currentState.pledge, amount: toPennies(pledgeAmount) },
        }))

        console.log({ paymentMethod })

        if (submitable) navigate((!paymentMethod || paymentMethod.token == "") ? '../pledge/pm' : '../pledge/review')
        else throw new Error(`Pledge Amounts must be more than $1`)
    }

    function handleBack() {
        navigate(-1)
    }

    if (isOrganizer || hasPledge || groupStatus !== 'active') return <Navigate to={`/group/${group.id}`} replace={true} />
    else if (group.type === 'pending') return <Navigate to={`/account`} replace={true} />
    else if (group.type === 'fixed' || (group.type === 'merchant' && !!group.organizer)) return <Navigate to={`/group/${group.id}/welcome`} replace={true} />
    else return (<>
        {group.type === 'both' &&
            <Column.Header.Start>
                <a onClick={handleBack}>
                    <img src="/icons/arrow-left.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
                </a>
            </Column.Header.Start>
        }
        <Column.Content className="group-contribute-screen">
            <div className='group-banner-section' style={{ marginBottom: '1rem' }}>
                <GroupBanner group={group} isGroupMember={true} />
                <h4 style={{ color: 'var(--splitcard-gray-500)', textAlign: 'center' }}>{(group.type === 'merchant' && group.organizer === null) ? group.merchant?.name || 'Someone' : (`${group.organizer?.givenName || ''} ${group.organizer?.familyName || ''}`.trim() || 'Someone')} is Inviting You To Contribute!</h4>
            </div>
            <GroupProgressBar goal={group.targetAmount || 0 / 100} pledge={((group.collectedAmount || 0) / 100) + ((toPennies(pledgeAmount) || 0) / 100)} />
            {isParticipantOrganizer && (
                <SplitRecommendationCard
                    peopleSplittingCount={peopleSplittingCount}
                    recommendedContribution={recommendedContribution}
                    onDecreasePeopleCount={decreasePeopleSplittingCount}
                    onIncreasePeopleCount={increasePeopleSplittingCount}
                    onApplyRecommendedAmount={applyRecommendedAmount}
                />
            )}
            <Form id={formId} onSubmit={handleSubmit} className="flex flex-col" style={{ gap: '.5rem' }}>
                <h3 style={{ margin: '1rem 0', textAlign: 'center' }}>Enter Your Contribution</h3>
                <div className="flex flex-row items-center" style={{ width: '100%', padding: '0.625rem 1.25rem', gap: 'inherit', backgroundColor: 'var(--splitcard-gray-50)', border: '1px solid var(--splitcard-gray-100)', borderRadius: 'var(--border-radius-md)' }}>
                    <Avatar user={clerkUser} />
                    <p className="text-body-2"><strong>{clerkUser?.fullName || 'Me'}</strong></p>
                </div>
                <CurrencyTextField centered={true} {...pledgeAmountProps} required />
                {group.type !== 'merchant' && <div style={{ marginTop: '0.75rem' }}>
                    <div className='text-gray-500 text-center'>
                        <p className="text-body-2">Charge adjusts if less than total collected is spent.</p>
                        <p className="text-body-2">You are only charged when the group card is used!</p>
                    </div>
                </div>}
            </Form>
            {group.type !== 'merchant' && <div className="flex flex-col gap-4">
                <h3 style={{ margin: '1rem 0', textAlign: 'center' }}>Who's Joined</h3>
                <PledgeList group={group} showUnclaimedParticipants={isOrganizer} showParticipantLabel={false} showOutline={true} listType="claimed" />
            </div>}
            <Column.Footer>
                <Button form={formId} type="submit" disabled={!submitable}>Next</Button>
            </Column.Footer>
        </Column.Content>
        <dialog ref={participantOrganizerRef} onClose={markParticipantOrganizerModalSeen}>
            <div>
                <div className="participant-organizer-modal">
                    <div className='participant-organizer-how-it-works'>
                        <div className='participant-organizer-banner'>
                            <div className="bubble-sml flex items-center justify-center shrink-0" style={{ background: 'var(--splitcard-green-200)' }}>
                                <Icons.Store tint="var(--splitcard-green-600)" width="1.5rem" height="1.5rem" />
                            </div>
                            <div className='participant-organizer-banner-copy'>
                                <p className='text-body'><strong>{group.merchant?.name || 'Someone'} has created a Splitcard for you.</strong></p>
                                <p className='text-body-2'>You&apos;re the first contributor. Once you join, you&apos;ll become the organizer and can invite others in one tap.</p>
                            </div>
                        </div>
                        <ol className='participant-organizer-steps'>
                            {participantOrganizerSteps.map((step, index) => (
                                <li className='participant-organizer-step' key={`${step.label}-${index}`}>
                                    <div className='participant-organizer-step-badge'>{index + 1}</div>
                                    <div className='participant-organizer-step-content'>
                                        <p className='text-body-2 participant-organizer-step-label'><strong>{step.label}</strong></p>
                                        <p className='text-body participant-organizer-step-description'>{step.description}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                    <Button theme="primary" formMethod="dialog" onClick={closeParticipantOrganizerModal}>Got it</Button>
                </div>
            </div>
        </dialog>
    </>)
}

const loader = createAuthenticatedLoader(async ({ params }, Clerk) => {
    await refreshClerkUser(Clerk)
    const userLimits = parseUserLimits(Clerk.user?.publicMetadata?.limits)
    if (!userLimits.joinGroups && params.groupId) return redirect(`/group/${params.groupId}`)

    return json(null, 200)
})

const ContributeRoute: RouteObject = {
    path: "contribute",
    element: <GroupContributeScreen />,
    shouldRevalidate: () => false,
    loader,
}

export default ContributeRoute
